import { Suspense, useEffect, useMemo } from "react";
import { useSearch, createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { useCategory, useWordsByPages } from "@/lib/api/vocabulary";
import {
  chunkIntoSheets,
  isPrintMode,
  toPageRange,
  type PrintMode,
} from "@/shared/lib/vocabulary-print";
import type { Word } from "@growing/api";
import { buildMockWords, IS_DEV } from "@/shared/lib/vocabulary-print-dev-mock";

interface PrintSearch {
  categoryId?: string;
  from?: string;
  to?: string;
  mode?: string;
  perSheet?: string;
  mock?: string;
}

/** 시험지 모드에서 답을 적을 빈 칸 */
function BlankCell() {
  return <span className="inline-block w-full border-b border-gray-400" />;
}

/**
 * 한 장의 단어 수에 따른 글자 크기.
 * 행 간격은 `justify-between`이 지면 높이에 맞춰 균등 배분하므로,
 * 여기서는 "행이 서로 겹치지 않을 만큼" 글자만 줄인다.
 * A4 인쇄 가능 높이(297mm - 여백 30mm ≈ 1009px)에서 로고/페이지 번호/이름란을 뺀 값 기준.
 */
function getRowFontSize(wordCount: number, mode: PrintMode) {
  const PRINTABLE_PX = 1009;
  const LOGO_AND_FOOTER_PX = 108;
  const NAME_ROW_PX = 76; // 시험지 모드의 이름/점수란

  const available =
    PRINTABLE_PX - LOGO_AND_FOOTER_PX - (mode === "full" ? 0 : NAME_ROW_PX);
  const perRow = available / wordCount;

  if (perRow >= 30) return "text-[19px] leading-7";
  if (perRow >= 24) return "text-[16px] leading-6";
  if (perRow >= 19) return "text-[14px] leading-5";
  return "text-[11px] leading-4";
}

function WordRow({
  word,
  mode,
  density,
}: {
  word: Word;
  mode: PrintMode;
  density: string;
}) {
  return (
    <div className={`flex items-end ${density}`}>
      {/* 번호는 채점을 위해 시험지 모드에서만 표시 (학습용은 예전 인쇄물과 동일하게 생략) */}
      {mode !== "full" && (
        <span className="w-8 shrink-0 pr-2 text-right text-gray-500">
          {word.number}
        </span>
      )}
      <span className="w-[45%] shrink-0">
        {mode === "hide-word" ? <BlankCell /> : word.word}
      </span>
      <span className="flex-1">
        {mode === "hide-meaning" ? <BlankCell /> : word.meaning}
      </span>
    </div>
  );
}

function PrintView() {
  const searchParams = useSearch({ strict: false }) as PrintSearch;

  const categoryId = searchParams.categoryId ?? "";
  const from = Number(searchParams.from ?? 0);
  const to = Number(searchParams.to ?? 0);
  const modeParam = searchParams.mode ?? null;
  const mode: PrintMode = isPrintMode(modeParam) ? modeParam : "full";
  // 장당 단어 수 (미지정/0이면 세트 전체를 한 장에)
  const perSheet = Number(searchParams.perSheet ?? 0) || 0;

  const pages = useMemo(() => toPageRange(from, to), [from, to]);

  // 개발 모드에서 ?mock=<페이지당 단어 수>로 레이아웃만 확인 (로그인/백엔드 불필요)
  const mockPerPage = Number(searchParams.mock ?? 0);
  const isMock = IS_DEV && Number.isInteger(mockPerPage) && mockPerPage > 0;

  const { data: fetchedCategory } = useCategory(isMock ? "" : categoryId);
  const {
    data: fetchedWords,
    isLoading: isFetching,
    isError,
  } = useWordsByPages(isMock ? "" : categoryId, pages);

  const category = isMock ? { name: "목데이터" } : fetchedCategory;
  const words = isMock ? buildMockWords(pages, mockPerPage) : fetchedWords;
  const isLoading = isMock ? false : isFetching;

  // 브라우저 인쇄 대화상자의 기본 파일명이 되는 문서 제목
  useEffect(() => {
    if (!category) return;
    const range = from === to ? `Page ${from}` : `Page ${from}-${to}`;
    document.title = `Growing English ${category.name} ${range}`;
  }, [category, from, to]);

  /**
   * 세트(page)별로 묶은 뒤 장당 단어 수로 다시 나눈다.
   * 세트 경계를 넘지 않으므로 서로 다른 세트가 한 장에 섞이지 않는다.
   */
  const sheets = useMemo(() => {
    const grouped = new Map<number, Word[]>();
    for (const page of pages) grouped.set(page, []);
    for (const word of words ?? []) {
      grouped.get(word.page)?.push(word);
    }

    return pages.flatMap((page) => {
      const pageWords = (grouped.get(page) ?? []).sort(
        (a, b) => a.number - b.number,
      );
      if (pageWords.length === 0) {
        return [{ page, indexInPage: 0, sheetCount: 1, words: [] }];
      }
      const chunks = chunkIntoSheets(pageWords, perSheet);
      return chunks.map((chunk, i) => ({
        page,
        indexInPage: i,
        sheetCount: chunks.length,
        words: chunk,
      }));
    });
  }, [words, pages, perSheet]);

  if ((!categoryId && !isMock) || pages.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center text-center">
        <p>인쇄할 범위가 올바르지 않습니다. 창을 닫고 다시 시도해주세요.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p>단어를 불러오는 중...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center text-center">
        <p>단어를 불러오지 못했습니다. 로그인 상태를 확인한 뒤 다시 시도해주세요.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 py-8 print:bg-white print:py-0">
      {/* 화면에서만 보이는 조작 버튼 */}
      <div className="fixed top-4 right-4 z-50 flex gap-2 print:hidden">
        <Button onClick={() => window.print()}>인쇄하기</Button>
        <Button variant="outline" onClick={() => window.close()}>
          닫기
        </Button>
      </div>

      {sheets.map((sheet) => {
        const pageWords = sheet.words;

        return (
          <div
            key={`${sheet.page}-${sheet.indexInPage}`}
            className="print-page flex flex-col"
          >
            {/* 우측 상단 로고 (예전 인쇄물과 동일한 위치) */}
            <div className="mb-4 flex justify-end">
              <img
                src="/growing-english-logo.jpg"
                alt="Growing English"
                className="h-10"
              />
            </div>

            {/* 시험지 모드에서 이름/점수를 적을 자리 */}
            {mode !== "full" && (
              <div className="mb-6 flex gap-8 text-sm text-gray-600">
                <span className="flex-1">
                  이름: <span className="inline-block w-40 border-b border-gray-400" />
                </span>
                <span>
                  점수: <span className="inline-block w-20 border-b border-gray-400" />
                </span>
              </div>
            )}

            {/* justify-between으로 남는 높이를 행 사이에 균등 배분해 지면을 채운다 */}
            <div className="flex flex-1 flex-col justify-between">
              {pageWords.length === 0 ? (
                <p className="text-sm text-gray-500">
                  이 페이지에는 단어가 없습니다.
                </p>
              ) : (
                pageWords.map((word) => (
                  <WordRow
                    key={word._id}
                    word={word}
                    mode={mode}
                    density={getRowFontSize(pageWords.length, mode)}
                  />
                ))
              )}
            </div>

            {/* 하단 중앙 페이지 번호 (한 세트가 여러 장이면 1-1, 1-2로 구분) */}
            <div className="pt-4 text-center text-sm text-gray-600">
              -{" "}
              {sheet.sheetCount > 1
                ? `${sheet.page}-${sheet.indexInPage + 1}`
                : sheet.page}{" "}
              -
            </div>
          </div>
        );
      })}

      <style>{`
        .print-page {
          box-sizing: border-box;
          width: 210mm;
          height: 297mm;
          margin: 0 auto 2rem auto;
          padding: 15mm;
          background: white;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
        }

        @media print {
          @page {
            size: A4;
            margin: 15mm;
          }

          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }

          /*
           * @page 여백(15mm)을 뺀 인쇄 영역과 같은 높이를 명시해야
           * justify-between이 남는 높이를 행 사이에 배분할 수 있다.
           * height를 지우면 내용이 지면 위쪽에만 몰린다.
           * 297mm - 여백 30mm = 267mm이지만, 반올림 때문에 빈 페이지가 끼는 걸 막으려고
           * 1mm 여유를 둔다.
           */
          .print-page {
            width: auto;
            height: 266mm;
            margin: 0;
            padding: 0;
            box-shadow: none;
            overflow: hidden;
            page-break-after: always;
          }

          .print-page:last-child {
            page-break-after: auto;
          }
        }
      `}</style>
    </div>
  );
}

function VocabularyPrintPage() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center">
          <p>준비 중...</p>
        </div>
      }
    >
      <PrintView />
    </Suspense>
  );
}

export const Route = createFileRoute("/vocabulary/print")({
  component: VocabularyPrintPage,
});
